import { useQuery } from "@tanstack/react-query";
import i18n from "i18next";
import { haversineKm } from "@/lib/geo";
import { supabase } from "@/lib/supabase";

export type TargetCityRow = {
  id: string;
  city_key: string;
  name: string;
  name_de: string | null;
  name_fr: string | null;
  name_it: string | null;
  country_code: string;
  lat: number;
  lng: number;
  timezone: string;
  status: string;
  crawl_enabled: boolean;
  sort_order: number;
  launched_at: string | null;
};

export const DEFAULT_CITY_KEY = "zurich";

/** Within this radius (km) of an active city center counts as inside that market. */
export const ACTIVE_CITY_GEOFENCE_KM = 25;

export const GPS_MARKET_MISMATCH_DISMISS_KEY = "pulse.gpsMarketMismatch.dismissed";

export const ACTIVE_TARGET_CITIES_QUERY_KEY = ["target_cities", "active"] as const;

export const ALL_TARGET_CITIES_QUERY_KEY = ["target_cities", "all"] as const;

let allCitiesCache: TargetCityRow[] = [];
let activeCitiesCache: TargetCityRow[] = [];

export function getActiveCitiesCache(): TargetCityRow[] {
  return activeCitiesCache;
}

export async function fetchAllTargetCities(): Promise<TargetCityRow[]> {
  const { data, error } = await supabase.rpc("get_target_cities");
  if (error) throw error;
  allCitiesCache = (data ?? []) as TargetCityRow[];
  activeCitiesCache = allCitiesCache.filter((row) => row.status === "active");
  return allCitiesCache;
}

export function useAllTargetCities() {
  return useQuery({
    queryKey: ALL_TARGET_CITIES_QUERY_KEY,
    queryFn: fetchAllTargetCities,
    staleTime: 5 * 60_000,
  });
}

export function searchTargetCities(
  cities: TargetCityRow[],
  query: string,
  limit = 20,
): TargetCityRow[] {
  const q = query.trim().toLowerCase();
  const sorted = [...cities].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  if (!q) return sorted.slice(0, limit);

  return sorted
    .filter((city) => {
      const labels = [city.name, city.name_de, city.name_fr, city.name_it, city.city_key]
        .filter(Boolean)
        .map((label) => label!.toLowerCase());
      return labels.some((label) => label.includes(q));
    })
    .slice(0, limit);
}

export async function fetchActiveTargetCities(): Promise<TargetCityRow[]> {
  const all = allCitiesCache.length > 0 ? allCitiesCache : await fetchAllTargetCities();
  activeCitiesCache = all.filter((row) => row.status === "active");
  return activeCitiesCache;
}

export function useActiveTargetCities() {
  return useQuery({
    queryKey: ACTIVE_TARGET_CITIES_QUERY_KEY,
    queryFn: fetchActiveTargetCities,
    staleTime: 5 * 60_000,
  });
}

export function displayNameForTargetCity(city: TargetCityRow, lang = i18n.language): string {
  if (lang.startsWith("de") && city.name_de) return city.name_de;
  if (lang.startsWith("fr") && city.name_fr) return city.name_fr;
  if (lang.startsWith("it") && city.name_it) return city.name_it;
  return city.name;
}

export function getCityCenterByKey(cityKey: string): { lat: number; lng: number } | null {
  const city =
    activeCitiesCache.find((row) => row.city_key === cityKey) ??
    allCitiesCache.find((row) => row.city_key === cityKey);
  if (!city) return null;
  return { lat: city.lat, lng: city.lng };
}

export function resolveStoredCityKey(
  stored: string | null,
  activeCities: TargetCityRow[],
): string {
  if (!stored?.trim()) {
    return activeCities[0]?.city_key ?? DEFAULT_CITY_KEY;
  }
  const trimmed = stored.trim();
  const byKey = activeCities.find((city) => city.city_key === trimmed);
  if (byKey) return byKey.city_key;

  const lower = trimmed.toLowerCase();
  const byName = activeCities.find(
    (city) =>
      city.name.toLowerCase() === lower ||
      city.name_de?.toLowerCase() === lower ||
      city.name_fr?.toLowerCase() === lower ||
      city.name_it?.toLowerCase() === lower,
  );
  if (byName) return byName.city_key;

  return activeCities[0]?.city_key ?? DEFAULT_CITY_KEY;
}

export function findNearestActiveCityKey(
  lat: number,
  lng: number,
  activeCities: TargetCityRow[],
): string {
  if (activeCities.length === 0) return DEFAULT_CITY_KEY;

  let nearest = activeCities[0]!.city_key;
  let minKm = Infinity;

  for (const city of activeCities) {
    const km = haversineKm(lat, lng, city.lat, city.lng);
    if (km < minKm) {
      minKm = km;
      nearest = city.city_key;
    }
  }

  return nearest;
}

export function searchActiveTargetCities(
  activeCities: TargetCityRow[],
  query: string,
  limit = 8,
): TargetCityRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return activeCities.slice(0, limit);

  return activeCities
    .filter((city) => {
      const labels = [city.name, city.name_de, city.name_fr, city.name_it, city.city_key]
        .filter(Boolean)
        .map((label) => label!.toLowerCase());
      return labels.some((label) => label.includes(q));
    })
    .slice(0, limit);
}

export function isInsideActiveCityMarket(
  lat: number,
  lng: number,
  city: TargetCityRow,
  radiusKm = ACTIVE_CITY_GEOFENCE_KM,
): boolean {
  return haversineKm(lat, lng, city.lat, city.lng) <= radiusKm;
}

export function isGpsOutsideActiveMarket(
  lat: number,
  lng: number,
  nearestCityKey: string,
  activeCities: TargetCityRow[],
): boolean {
  const nearest = activeCities.find((city) => city.city_key === nearestCityKey);
  if (!nearest) return false;
  return !isInsideActiveCityMarket(lat, lng, nearest);
}

export type GpsMarketMismatch = {
  detectedPlaceName: string;
  activeCityLabel: string;
};

export function isGpsMarketMismatchDismissed(): boolean {
  try {
    return sessionStorage.getItem(GPS_MARKET_MISMATCH_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissGpsMarketMismatch(): void {
  try {
    sessionStorage.setItem(GPS_MARKET_MISMATCH_DISMISS_KEY, "1");
  } catch {}
}
